import React, { useMemo, useState, useEffect } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { timeSeries } from "../../lib/aggregateAnalytics";
import { resolveChartRange } from "../../lib/dateRange";

const PRESETS = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "All", days: null },
];

// Colors per competition title
const COMP_COLORS = {
  total: "#ff4d4d",
  Algorithm: "#4da6ff",
  "Real-world Problem Solving": "#a855f7",
  "Web Development": "#22d3ee",
  "Photo Manipulation": "#f59e0b",
  "Poster Design": "#ec4899",
  "Logo Design": "#84cc16",
  "UI/UX Design": "#f97316",
  "Fastest Line Following Bot": "#10b981",
  "Innovation Challenge": "#6366f1",
  "Inter School Quiz": "#06b6d4",
  "Most Popular ICT Society": "#e879f9",
  Legacy: "#888888",
};

const FALLBACK_COLORS = [
  "#4da6ff","#a855f7","#22d3ee","#f59e0b","#ec4899",
  "#84cc16","#f97316","#10b981","#6366f1","#06b6d4",
];

const SHORT_LABEL = {
  total: "Total",
  Algorithm: "Algo",
  "Real-world Problem Solving": "Problem Solving",
  "Web Development": "Web Dev",
  "Photo Manipulation": "Photo",
  "Poster Design": "Poster",
  "Logo Design": "Logo",
  "UI/UX Design": "UI/UX",
  "Fastest Line Following Bot": "Line Bot",
  "Innovation Challenge": "Innovation",
  "Inter School Quiz": "Quiz",
  "Most Popular ICT Society": "ICT Society",
  Legacy: "Legacy",
};

function getColor(title, idx) {
  return COMP_COLORS[title] ?? FALLBACK_COLORS[idx % FALLBACK_COLORS.length];
}

/** Safe CSS id: no spaces/special chars */
function cssId(s) {
  return "tl__" + s.replace(/[^a-zA-Z0-9]/g, "_");
}

function useMobile(bp = 640) {
  const [m, setM] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia(`(max-width:${bp}px)`).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width:${bp}px)`);
    const h = (e) => setM(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, [bp]);
  return m;
}

export default function RegistrationTimeline({
  records,
  title = "Registrations Over Time",
}) {
  const isMobile = useMobile();
  const [granularity, setGranularity] = useState("day");
  const [presetLabel, setPresetLabel] = useState("30d");
  const [activeSources, setActiveSources] = useState(() => new Set(["total"]));

  const preset = PRESETS.find((p) => p.label === presetLabel) ?? PRESETS[1];

  const { from, to } = useMemo(
    () => resolveChartRange(preset, records),
    [preset, records]
  );

  // Build the full source list: "total" + every distinct competition title
  const allSources = useMemo(() => {
    const comps = [
      ...new Set(records.map((r) => r.competitionTitle).filter(Boolean)),
    ].sort();
    return [
      { key: "total", label: "Total", color: "#ff4d4d" },
      ...comps.map((t, i) => ({
        key: t,
        label: SHORT_LABEL[t] ?? t,
        color: getColor(t, i),
      })),
    ];
  }, [records]);

  const toggleSource = (key) => {
    setActiveSources((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size === 1) return prev; // keep at least one active
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Build per-source time series, then merge into one array per date key
  const chartData = useMemo(() => {
    const srcs = [...activeSources];
    if (!from || !to || srcs.length === 0) return [];

    const seriesMaps = {};
    let baseKeys = null;

    for (const src of srcs) {
      const filtered =
        src === "total"
          ? records
          : records.filter((r) => r.competitionTitle === src);
      const series = timeSeries(filtered, granularity, from, to);
      seriesMaps[src] = new Map(series.map((d) => [d.key, d.count]));
      if (!baseKeys) baseKeys = series.map((d) => d.key);
    }

    if (!baseKeys) return [];

    return baseKeys.map((key) => {
      const point = { key };
      for (const src of srcs) {
        point[src] = seriesMaps[src].get(key) ?? 0;
      }
      return point;
    });
  }, [activeSources, records, granularity, from, to]);

  const activeSrcList = allSources.filter((s) => activeSources.has(s.key));

  const formatKey = (key) => {
    if (granularity === "week") return key;
    const parts = key.split("-");
    if (parts.length === 3) {
      const d = new Date(
        Number(parts[0]),
        Number(parts[1]) - 1,
        Number(parts[2])
      );
      return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
    }
    return key;
  };

  const tickInterval =
    chartData.length > 45
      ? Math.floor(chartData.length / 12)
      : chartData.length > 20
      ? 2
      : 0;

  const chartHeight = isMobile ? 160 : 220;

  return (
    <div className="analytics-card">
      <div className="analytics-card-header">
        <h3 className="analytics-card-title">{title}</h3>
        <div className="timeline-controls">
          <div className="toggle-group">
            {["day", "week"].map((g) => (
              <button
                key={g}
                type="button"
                className={`toggle-btn ${granularity === g ? "active" : ""}`}
                onClick={() => setGranularity(g)}
              >
                {g.charAt(0).toUpperCase() + g.slice(1)}
              </button>
            ))}
          </div>
          <div className="toggle-group">
            {PRESETS.map((p) => (
              <button
                key={p.label}
                type="button"
                className={`toggle-btn ${
                  presetLabel === p.label ? "active" : ""
                }`}
                onClick={() => setPresetLabel(p.label)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Source toggles: Total + per-competition */}
      <div className="source-toggles-wrap">
        {allSources.map((src) => (
          <button
            key={src.key}
            type="button"
            className={`source-toggle-btn ${
              activeSources.has(src.key) ? "active" : ""
            }`}
            style={{ "--src-color": src.color }}
            onClick={() => toggleSource(src.key)}
            title={src.key === "total" ? "All registrations combined" : src.key}
          >
            <span className="source-toggle-dot" />
            {src.label}
          </button>
        ))}
      </div>

      {chartData.length === 0 ? (
        <p className="analytics-empty">No data in selected range.</p>
      ) : (
        <ResponsiveContainer width="100%" height={chartHeight}>
          <AreaChart
            data={chartData}
            margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
          >
            <defs>
              {activeSrcList.map((src) => (
                <linearGradient
                  key={src.key}
                  id={cssId(src.key)}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor={src.color}
                    stopOpacity={activeSrcList.length > 1 ? 0.25 : 0.4}
                  />
                  <stop offset="95%" stopColor={src.color} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="rgba(255,255,255,0.08)"
            />
            <XAxis
              dataKey="key"
              tickFormatter={formatKey}
              tick={{ fill: "#aaa", fontSize: isMobile ? 10 : 11 }}
              interval={tickInterval}
            />
            <YAxis
              tick={{ fill: "#aaa", fontSize: isMobile ? 10 : 11 }}
              allowDecimals={false}
              width={28}
              domain={[0, "auto"]}
            />
            <Tooltip
              contentStyle={{
                background: "#1a1a2e",
                border: "1px solid #ff4d4d",
                borderRadius: 6,
                color: "#fff",
                fontSize: 12,
              }}
              labelFormatter={formatKey}
              formatter={(v, name) => [
                v,
                name === "total" ? "Total" : SHORT_LABEL[name] ?? name,
              ]}
            />
            {activeSrcList.map((src) => (
              <Area
                key={src.key}
                type="monotone"
                dataKey={src.key}
                stroke={src.color}
                strokeWidth={activeSrcList.length > 1 ? 1.5 : 2}
                fill={`url(#${cssId(src.key)})`}
                dot={false}
                activeDot={{ r: 3, fill: src.color }}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
